import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Building2, Search, Users, HandCoins, FileText, UserPlus } from "lucide-react";

export default function Accounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/accounts").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter((c) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return (c.company_name || "").toLowerCase().includes(needle) || (c.industry || "").toLowerCase().includes(needle);
  });

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Customer Accounts</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">360° customer view.</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Every company in one place — contacts, leads, quotations, deals and complete interaction history.</p>
      </header>

      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company or industry…" className="pl-10 h-11 rounded-2xl bg-muted/40" data-testid="accounts-search" />
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-3xl" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-3xl border-border/60 shadow-soft"><CardContent className="p-12 text-center text-sm text-muted-foreground">No customer accounts found.</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {filtered.map((c) => (
            <Link key={c.id} to={`/accounts/${c.id}`} data-testid={`account-card-${c.id}`}>
              <Card className="rounded-3xl border-border/60 shadow-soft card-lift h-full">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-extrabold text-base truncate">{c.company_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[c.industry, c.location || [c.district, c.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}
                      </p>
                      {c.owner_name && <p className="text-[11px] text-muted-foreground mt-0.5">Owner: <span className="font-semibold text-foreground/80">{c.owner_name}</span></p>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <Badge variant="outline" className="text-[10px] gap-1"><Users className="h-3 w-3" /> {c.contacts_count} contacts</Badge>
                    <Badge variant="outline" className="text-[10px] gap-1"><UserPlus className="h-3 w-3" /> {c.leads_count} leads</Badge>
                    <Badge variant="outline" className="text-[10px] gap-1"><FileText className="h-3 w-3" /> {c.quotations_count} quotes</Badge>
                    <Badge variant="outline" className="text-[10px] gap-1"><HandCoins className="h-3 w-3" /> {c.deals_count} deals</Badge>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
